import * as Sentry from '@sentry/nextjs';
import {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPage,
} from 'next';

import { addLocalePrefix } from 'utils/routes.utils';

import { Duration } from 'constants/duration.constants';
import { NOT_FOUND } from 'constants/nextjs.constants';
import { Routes } from 'constants/routes.constants';
import { pageEventQuery } from 'queries/events/queries';
import { usePageEventByQuery } from 'queries/events/usePageEventByQuery';
import { ticketsEventIdByDatoEventSlugQuery } from 'queries/tickets/queries';
import {
  getEventKey,
  getTicketAndExtraTicketIds,
  ticketsEventCategoriesQuery,
  ticketsEventQuery,
  ticketsInfoQuery,
} from 'restful/tickets';
import Prefetcher from 'services/prefetcher';
import { CartStoreProvider } from 'services/stores/cart/CartStoreProvider';

import EventDetailCartContainer from 'components/@events/EventDetailCartContainer';
import { isEventTicketingEnabled, isPastEvent } from 'components/@events/utils';
import ExpiredDialog from 'components/@seating/ExpiredDialog';
import PageSeo from 'components/@seo/PageSeo';

const EventCart: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  eventKey,
  categories,
  filters,
  ticketingError,
}) => {
  const { event } = usePageEventByQuery();

  if (!event) return null;

  return (
    <CartStoreProvider key={eventKey} eventKey={eventKey}>
      <PageSeo tags={event.seoTags} nofollow noindex />
      <EventDetailCartContainer
        event={event}
        categories={categories}
        filters={filters}
        ticketingError={ticketingError}
      />
      <ExpiredDialog />
    </CartStoreProvider>
  );
};

export default EventCart;

// We explicitly don't want to generate pages at build time because we use redirects in getStaticProps.
// stackoverflow.com/questions/66886040/trying-to-redirect-from-getstaticprops-returns-error-during-build
export const getStaticPaths: GetStaticPaths = () => {
  return { paths: [], fallback: 'blocking' };
};

export const getStaticProps = async ({
  locale,
  params,
  preview,
}: GetStaticPropsContext<{ slug: string }>) => {
  const prefetcher = await Prefetcher.init({ locale, preview });
  const slug = params?.slug;

  if (!slug) return { ...NOT_FOUND };

  const [{ pageEvent }] = await Promise.all([
    prefetcher.prefetch(
      pageEventQuery,
      { filter: { slug: { eq: slug } } },
      { meta: { dehydrate: true } },
    ),
    prefetcher.prefetch(
      ticketsEventIdByDatoEventSlugQuery,
      { slug },
      { meta: { dehydrate: true } },
    ),
  ]);

  if (!pageEvent) return { ...NOT_FOUND };

  const pastEvent = isPastEvent(pageEvent);

  if (!isEventTicketingEnabled(pageEvent) || pastEvent)
    return {
      redirect: {
        destination: addLocalePrefix(locale, Routes.EventDetail, { slug }),
        permanent: false,
      },
      revalidate: Duration.TEN_MIN,
    };

  const eventId = pageEvent.ticketsEventId;

  // In case something is off with ticketing, redirect to the event page
  // This way we don't show a 500 error to the user
  try {
    const [{ event }, { categories, filters }] = await Promise.all([
      prefetcher.prefetch(
        ticketsEventQuery,
        { id: eventId, timeslotId: pageEvent.ticketsTimeslotId },
        { meta: { dehydrate: true } },
      ),
      prefetcher.prefetch(ticketsEventCategoriesQuery, {
        id: eventId,
        timeslotId: pageEvent.ticketsTimeslotId,
        locale,
      }),
    ]);

    if (!event) return { ...NOT_FOUND };

    const ticketIds = getTicketAndExtraTicketIds(event);

    if (!ticketIds.length) return { ...NOT_FOUND };

    await prefetcher.prefetch(
      ticketsInfoQuery,
      { eventId, ticketIds },
      { meta: { dehydrate: true } },
    );

    return {
      props: {
        eventKey: getEventKey(
          pageEvent.ticketsEventId,
          pageEvent.ticketsTimeslotId,
        ),
        categories,
        filters,
        dehydratedState: prefetcher.dehydratedState,
      },
      revalidate: Duration.TEN_MIN,
    };
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);

    return {
      props: {
        eventKey: getEventKey(
          pageEvent.ticketsEventId,
          pageEvent.ticketsTimeslotId,
        ),
        categories: [],
        filters: [],
        ticketingError: true,
        dehydratedState: prefetcher.dehydratedState,
      },
      revalidate: Duration.TEN_MIN,
    };
  }
};
